/**
 * O que a tela mostra para cada pessoa. É conveniência: o gate real é do backend,
 * que decide `manage` e `audit` na plataforma (`has_permission(..., None, ...)`) —
 * por isso a pergunta aqui é `podeNaPlataforma`, e não o `can` do tenant do seletor.
 */
import { podeNaPlataforma, useAuthStore } from '@/store/auth'

export interface AcessoDaKb {
  /** `assistant_kb:manage`: cria, edita, move e exclui nas organizações que enxerga */
  gere: boolean
  /**
   * Vê "Detalhes" (perfis e contagens) DAS PASTAS QUE JÁ ENXERGA: `manage`,
   * `audit` ou admin (D10). Não abre a árvore: quem só audita passou a ver apenas a
   * árvore navegável dele (30/09/2026), e o backend confere isso de novo.
   */
  veDetalhes: boolean
  /** Admin: único que gere a pasta da plataforma (D16) */
  admin: boolean
}

export function acessoDaKb(user: Parameters<typeof podeNaPlataforma>[0]): AcessoDaKb {
  const admin = podeNaPlataforma(user, 'admin', 'full')
  const gere = admin || podeNaPlataforma(user, 'assistant_kb', 'manage')
  const audita = podeNaPlataforma(user, 'assistant_kb', 'audit')
  return { gere, veDetalhes: gere || audita, admin }
}

export function useAcessoDaKb(): AcessoDaKb {
  const user = useAuthStore((s) => s.user)
  return acessoDaKb(user)
}

const MENSAGENS: Record<string, string> = {
  folder_name_taken: 'Já existe uma pasta com esse nome neste nível.',
  max_depth: 'Limite de profundidade de pastas atingido.',
  folder_cycle: 'Uma pasta não pode ir para dentro dela mesma.',
  organization_mismatch: 'A pasta de destino é de outra organização.',
  folder_not_empty: 'A pasta não está vazia: mova ou exclua os documentos e subpastas antes.',
  folder_not_found: 'Pasta não encontrada.',
  parent_not_found: 'Pasta de cima não encontrada.',
  template_not_found: 'Um dos perfis escolhidos não existe mais.',
  invalid_folder_name: 'Informe um nome para a pasta.',
  permission_denied: 'Você não tem permissão para esta ação.',
  organization_not_found: 'Organização não encontrada.',
  invalid_template_ids: 'Lista de perfis inválida.',
  invalid_folder_id: 'Pasta inválida.',
  document_not_found: 'Documento não encontrado.',
  nothing_to_update: 'Nada foi alterado.',
  document_indexing: 'O documento está sendo indexado agora; tente de novo quando terminar.',
  status_not_editable: 'O estado do documento só muda por Desativar ou Reindexar.',
  audit_unavailable: 'A trilha de auditoria está indisponível; a leitura de pasta sensível fica bloqueada até ela voltar.',
  kb_disabled: 'A Base de Conhecimento está desligada no momento.',
  tipo_arquivo_nao_suportado: 'Tipo de arquivo não suportado.',
  arquivo_vazio: 'O arquivo está vazio.',
  git_source_active: 'Documento gerado do Git: exclua a fonte em Repositórios antes de apagá-lo.',
  source_not_found: 'Repositório não encontrado.',
  source_not_excluded: 'Só um repositório excluído pode ser reativado.',
  source_excluded: 'Repositório excluído: reative antes de regerar.',
  invalid_rotulo: 'O nome na Base de Conhecimento tem de ter até 80 caracteres.',
  raiz_name_taken: 'Já existe uma empresa com esse nome.',
  invalid_raiz_nome: 'O nome da empresa tem de ter de 1 a 80 caracteres.',
  invalid_raiz_id: 'Empresa inválida.',
  invalid_secao: 'Seção inválida.',
  kb_raiz_not_found: 'Empresa não encontrada.',
  atalho_ja_existe: 'Esta pasta já tem um atalho para este arquivo.',
  pasta_do_original: 'O original já está nesta pasta.',
  atalho_not_found: 'Atalho não encontrado.',
}

export function mensagemDeErro(e: unknown, padrao: string): string {
  const detalhe = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detalhe === 'string') {
    const tamanho = /^arquivo_muito_grande:max=(\d+)MB$/.exec(detalhe)
    if (tamanho) return `Arquivo maior que o limite de ${tamanho[1]} MB.`
    return MENSAGENS[detalhe] ?? detalhe
  }
  // Erro nosso (validação antes da chamada) diz o motivo; o do axios não diz nada útil.
  if (e instanceof Error && e.message && !(e as { isAxiosError?: boolean }).isAxiosError) return e.message
  return padrao
}
